<script setup lang="ts">
import Button from 'primevue/button'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import LocationHeader from '@/components/location/LocationHeader.vue'
import AddressSearch from '@/components/start/AddressSearch.vue'
import AddressSuggestions from '@/components/start/AddressSuggestions.vue'
import MapCanvas from '@/components/start/MapCanvas.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import { useGeoPoint } from '@/composables/useGeoPoint'
import type { LngLat } from '@/composables/useLocation'
import { useMapPoint } from '@/composables/useMapPoint'
import { FLOW, track } from '@/utils/track'
import type { AddressHint } from '@/utils/yandexSearch'

const props = withDefaults(defineProps<Props>(), {
  saving: false,
  center: undefined,
  closable: false,
})

const emit = defineEmits<{
  confirm: [value: { coords: LngLat; address: string; house: string }]
  back: []
}>()

const { t } = useI18n()

/** Доставка: точка на карте, поиск адреса и подсказки. Пин приклеен к центру. */
interface Props {
  saving?: boolean
  /** Куда смотреть при открытии */
  center?: LngLat | undefined
  /** Экран открыт диалогом — нужна шапка с возвратом (в онбординге её нет) */
  closable?: boolean
}

const {
  coords,
  addressTitle,
  query,
  suggestions,
  isAddressPending,
  hasPoint,
  moveTo,
  search,
  pick,
  resolveAddress,
} = useMapPoint()

const { locate } = useGeoPoint()

const center = ref<LngLat>(props.center ?? [0, 0])

/** Ждём адрес для только что поставленной точки — кнопка в это время занята. */
const isConfirming = ref(false)

const canConfirm = computed(() => hasPoint.value && !props.saving && !isConfirming.value)

async function onPick(hint: AddressHint): Promise<void> {
  const point = await pick(hint)
  track(FLOW.mapSuggestPicked, { found: Boolean(point) })
  if (point) center.value = point
}

/** Пин в центре, значит точка известна ещё до первого сдвига карты. */
onMounted(() => moveTo(center.value))

async function findMe(): Promise<void> {
  const point = await locate()
  track(FLOW.mapLocateUsed, { found: Boolean(point) })
  if (point) center.value = point
}

/**
 * Подтверждение точки. Адрес сначала дожидаемся: с дебаунсом подпись на
 * экране отстаёт от пина, и, если отдать её как есть, в справочник уедут
 * новые координаты со старым названием улицы.
 */
async function confirm(): Promise<void> {
  if (!coords.value) return

  isConfirming.value = true
  const found = await resolveAddress()
  isConfirming.value = false

  const point = coords.value
  if (!point) return

  /*
   * Подпись-заглушку наружу не отдаём: из-за неё в справочник адресов попадал
   * текст «Укажите точку на карте» вместо адреса. Геокодер промолчал — отдаём
   * координаты: курьеру они полезнее и пустой строки, и надписи с экрана.
   */
  const fallback = `${point[1].toFixed(6)}, ${point[0].toFixed(6)}`

  emit('confirm', { coords: point, address: found.address || fallback, house: found.house })
}
</script>

<template>
  <section class="delivery-picker">
    <LocationHeader v-if="closable" :title="t('address.whereTo')" @back="emit('back')" />

    <div class="delivery-picker__body">
      <MapCanvas class="delivery-picker__canvas" :center="center" @moved="moveTo" />

      <div class="delivery-picker__search">
        <AddressSearch :model-value="query" @update:model-value="search" />
        <AddressSuggestions v-if="suggestions.length" :items="suggestions" @select="onPick" />
      </div>

      <footer class="delivery-picker__footer">
        <button
          type="button"
          class="delivery-picker__locate"
          :aria-label="t('address.findMe')"
          @click="findMe"
        >
          <AppIcon name="navigation-arrow" :size="24" />
        </button>

        <!-- Пока адрес не про текущий пин, показываем ожидание, а не прошлую улицу -->
        <p v-if="hasPoint || isAddressPending" class="delivery-picker__address font-18-b">
          {{ isAddressPending ? t('address.detecting') : addressTitle }}
        </p>
        <Button
          :label="t('common.done')"
          :disabled="!canConfirm"
          :loading="saving || isConfirming"
          class="delivery-picker__cta"
          @click="confirm"
        />
      </footer>
    </div>
  </section>
</template>

<style scoped lang="scss">
.delivery-picker {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;

  /* Карта на всю площадь: пин должен стоять в её центре, а не над футером */
  &__body {
    position: relative;
    flex: 1;
    min-height: 0;
  }

  &__canvas {
    position: absolute;
    inset: 0;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  }

  /* Поиск и подсказки — оверлей поверх карты, 16px от краёв */
  &__search {
    position: absolute;
    top: var(--screen-padding);
    right: var(--screen-padding);
    left: var(--screen-padding);
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__footer {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    padding: var(--screen-padding);
    padding-bottom: calc(29px + var(--tg-safe-area-bottom));
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
  }

  /* Кнопка геолокации висит ровно в 16px над футером */
  &__locate {
    position: absolute;
    right: var(--screen-padding);
    bottom: calc(100% + var(--screen-padding));
    display: flex;
    align-items: center;
    justify-content: center;
    width: 49px;
    height: 49px;
    padding: 0;
    border: 0;
    border-radius: var(--radius-round);
    background: var(--surface-inverse);
    color: var(--text-color-inverse);
    cursor: pointer;
  }

  &__address {
    margin-bottom: var(--screen-padding);
    line-height: 28px;
    text-align: center;
  }

  &__cta {
    width: 100%;
  }
}
</style>
